import classnames from 'classnames/bind'
import { AnimatePresence, m, useScroll } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import { GLOBAL_BREAKPOINTS } from '~/@types/breakpoints'
import { GlobalGridPreset } from '~/@types/grid-preset'

import { useNavBannerContext } from '~/components/Navigation/NavigationBanner'
import Image from '~/components/UI/Image'
import IndexListItem, {
  IndexListItemProps,
} from '~/components/UI/IndexList/IndexListItem'

import { useStyle } from '~/providers/StyleProvider'

import useBreakpoint from '~/hooks/useBreakpoint'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type IndexListProps = {
  className?: string
  items?: IndexListItemProps[]
  hasImageHasBackground?: boolean
  hasMultipleImages?: boolean
  hasAlphabetFilter?: boolean
  currentLetter?: string
}

export const ListImageLayout = {
  MULTIPLE: 'multiple',
  SINGLE: 'single',
} as const

const IMAGE_SIZES = {
  [ListImageLayout.MULTIPLE]: [
    { breakpoint: 'md', ratio: 1 / 5 },
    { ratio: 1 / 4 },
  ],
  [ListImageLayout.SINGLE]: [
    { breakpoint: 'md', ratio: 1 / 4 },
    { ratio: 6 / 16 },
  ],
}

function IndexList({
  className,
  hasImageHasBackground,
  hasMultipleImages,
  hasAlphabetFilter,
  currentLetter,
  items,
}: IndexListProps) {
  const listRef = useRef(null)
  const [isMounted, setIsMounted] = useState(false)
  const itemsRefs = useRef([])
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const isMobile = useBreakpoint(GLOBAL_BREAKPOINTS.MD)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isVisible] = useNavBannerContext()

  const { scrollY } = useScroll()

  const memoizedItems = useMemo(() => {
    return {
      listOffsetTop: listRef?.current?.offsetTop ?? 0,
      items: itemsRefs?.current?.map((item, index) => {
        const initialOffset = listRef?.current?.offsetTop
        const computedOffsetTop = item?.offsetTop - initialOffset

        return {
          index,
          topOffset: computedOffsetTop,
          bottomOffset: computedOffsetTop + item?.clientHeight,
        }
      }),
    }
  }, [
    itemsRefs,
    listRef,
    itemsRefs.current,
    listRef.current,
    isMounted,
    currentLetter,
  ])

  const onScroll = (y) => {
    const computedY = y

    memoizedItems?.items?.forEach((item) => {
      if (computedY >= item?.topOffset && computedY <= item?.bottomOffset) {
        setActiveIndex(item?.index)
      }
    })
  }

  useEffect(() => {
    if (memoizedItems?.items?.length > 0 && isMobile) {
      scrollY.onChange((latest) => {
        onScroll(latest)
      })
    }
  }, [memoizedItems])

  const onMouseEnter = (index) => {
    if (!isMobile) {
      setActiveIndex(index)
    }
  }

  const [loaded, setLoaded] = useState(0)
  const memoizedFilteredItems = useMemo(() => {
    return {
      items:
        hasAlphabetFilter && currentLetter
          ? items?.filter((item) => {
              return item.firstLetter === currentLetter
            })
          : items,
    }
  }, [hasAlphabetFilter, currentLetter])

  const activeImageSet =
    activeIndex !== null
      ? hasMultipleImages
        ? [
            memoizedFilteredItems?.items?.[activeIndex]?.cardImage,
            memoizedFilteredItems?.items?.[activeIndex]?.cardSecondImage,
          ]?.filter((image) => image)
        : [memoizedFilteredItems?.items?.[activeIndex]?.cardImage]
      : null

  useEffect(() => {
    if (hasAlphabetFilter && currentLetter) {
      setActiveIndex(0)
    }
  }, [hasAlphabetFilter, currentLetter])

  useIsomorphicLayoutEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    setLoaded(0)
  }, [activeIndex])

  const imageSizes = hasMultipleImages
    ? IMAGE_SIZES[ListImageLayout.MULTIPLE]
    : IMAGE_SIZES[ListImageLayout.SINGLE]

  const variants = {
    visible: {
      opacity: 1,
      transition: {
        duration: 0.25,
      },
    },
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.05,
      },
    },
  }

  return (
    <div
      className={cx(css.IndexList, className, {
        hasImageHasBackground,
        isBannerVisible: isVisible,
      })}>
      <div className={cx(gridStyle, css.grid)}>
        {activeImageSet && activeImageSet?.length > 0 && (
          <AnimatePresence>
            <m.div
              className={cx(css.images, {
                hasMultipleImages,
                hasImageHasBackground,
              })}
              initial={variants.hidden}
              variants={variants}
              animate={
                loaded === activeImageSet?.length ? 'visible' : 'hidden'
              }>
              {activeImageSet?.map((image, index) => {
                return (
                  <div
                    className={cx(css.imageWrapper, {})}
                    key={`image_${index}`}>
                    <Image
                      {...image}
                      layout={hasMultipleImages ? `fill` : `responsive`}
                      objectFit={hasMultipleImages ? `cover` : null}
                      sizesFromBreakpoints={imageSizes}
                      ratio={hasMultipleImages ? css.ratio : null}
                      className={css.imageItem}
                      priority={true}
                      asPlaceholder={false}
                      onLoadingComplete={() => setLoaded((e) => e + 1)}
                    />
                  </div>
                )
              })}
            </m.div>
          </AnimatePresence>
        )}

        {items && (
          <div className={css.list} ref={listRef}>
            <div className={css.marker}></div>
            {memoizedFilteredItems?.items?.map((item, index) => (
              <IndexListItem
                key={`index_list_item_${index}`}
                forwardRef={(element) => {
                  itemsRefs.current[index] = element
                }}
                onMouseEnter={onMouseEnter}
                isActive={activeIndex === index}
                index={index}
                className={css.item}
                {...item}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

IndexList.defaultProps = {}

export default IndexList
